import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Download } from 'lucide-react';
import styles from './MessageContextMenu.module.css';

export interface MenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

interface MessageContextMenuProps {
  open: boolean;
  position: MenuPosition;
  onCopy: () => void;
  onSaveImage: () => void;
  onClose: () => void;
}

export function MessageContextMenu({
  open,
  position,
  onCopy,
  onSaveImage,
  onClose,
}: MessageContextMenuProps) {
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleScroll = () => onClose();
    const handleResize = () => onClose();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  const handleCopy = () => {
    onCopy();
    onClose();
  };

  const handleSaveImage = () => {
    onSaveImage();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={menuRef}
          className={styles.menu}
          style={{
            top: position.top,
            bottom: position.bottom,
            left: position.left,
            right: position.right,
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <button className={styles.menuItem} onClick={handleCopy}>
            <Copy size={14} />
            <span>复制文本</span>
          </button>
          <div className={styles.divider} />
          <button className={styles.menuItem} onClick={handleSaveImage}>
            <Download size={14} />
            <span>保存为图片</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
